/**
 * 富文本编辑器指令
 */
import '@/styles/word-editor/style.scss';
import type {
  IDomEditor,
  IEditorConfig,
  SlateDescendant
} from '@wangeditor/editor';
import { SlateTransforms } from '@wangeditor/editor';
import { FileUploadPkConfig } from './file-util';
import fileUtil from './file-util';
import { uuid } from 'ele-admin-plus';

let id = 0;
/** 渲染队列 */
class RenderQueue<T> {
  private data: T[] = [];

  /** 添加元素 */
  enqueue(item: T): void {
    this.data.push(item);
  }

  dequeue(): T | undefined {
    return this.data.shift();
  }

  peek(): T | undefined {
    return this.data[0];
  }

  isEmpty(): boolean {
    return this.data.length === 0;
  }

  size(): number {
    return this.data.length;
  }
}

/** 渲染任务 */
const taskRenderQueue = new RenderQueue<{ id: number; fn: () => void }>();

// 消费渲染任务
function taskConsumer() {
  taskRenderQueue.dequeue()?.fn?.();
  globalThis.requestAnimationFrame(taskConsumer);
}
globalThis.requestAnimationFrame(taskConsumer);

/** 渲染等待函数 */
export async function renderEditorNext(func: (e: number) => void) {
  id += 1;
  const currId = id;
  return new Promise<void>((resolve, _reject) => {
    taskRenderQueue.enqueue({
      id: currId,
      fn: () => {
        try {
          func?.(currId);
        } catch (err) {
          console.log(err);
        }

        resolve();
      }
    });
  });
}

/**
 * input 单行输入框
 * textarea 多行文本输入框
 * number 数字收入框
 * richtext 富文本 默认
 */
export type LzdEditorType = 'input' | 'textarea' | 'number' | 'richtext';
export type LzdEditorValueFormat = 'json' | 'text' | 'html';

/** 富文本编辑器配置 */
export type LzdEditorConfig = IEditorConfig & {
  /** 编辑器类型 */
  type?: LzdEditorType;
  /** 输出格式 */
  format?: LzdEditorValueFormat;
  /** 编辑器名称 */
  name?: string;
  /** 最小高度 */
  minHeight?: string;
  /** 数据 */
  content?: LzdDescendant[];
  /** 值更新回调 */
  update?: (name: string, editor: IDomEditor) => void;
};

/** 富文本编辑器DOM */
export type LzdEditorElement = HTMLDivElement & {
  lzdEditorInstance?: IDomEditor | null;
  lzdEditorUpdate?: (func: () => void, time?: number) => void;
  focus?: () => void;
};

/** 富文本编辑器数据类型 */
export type LzdDescendant = SlateDescendant & {
  type?: string;
  text?: string;
  children?: LzdDescendant[];
};

/** 上传缓存 */
const UPLOAD_CACHE: Record<string, string> = {};
const UPLOAD_CACHE_KEY: string[] = [];

/** 获取缓存 */
function getCache(key: string) {
  return UPLOAD_CACHE[key];
}

/** 添加缓存 */
function putCache(key: string, value: string) {
  const cacheValue = getCache(key);
  if (cacheValue) {
    return cacheValue;
  }
  if (UPLOAD_CACHE_KEY.length > 20) {
    const removeKey = UPLOAD_CACHE_KEY.shift();
    if (removeKey) {
      delete UPLOAD_CACHE[removeKey];
    }
  }
  UPLOAD_CACHE_KEY.push(key);
  UPLOAD_CACHE[key] = value;

  return value;
}

/** 富文本编辑器默认配置 */
export function defaultEditorConfig(): LzdEditorConfig {
  return {
    type: 'richtext',
    scroll: false,
    readOnly: false,
    autoFocus: false,
    placeholder: '',
    customAlert(_info: string, _type: any) {},
    onCreated(_editor: IDomEditor) {},
    onChange(_editor: IDomEditor) {},
    onFocus(_editor: IDomEditor) {},
    onBlur(_editor: IDomEditor) {},
    MENU_CONF: {
      uploadImage: {
        async customUpload(file: File, insertFn: Function) {
          const fileReader = new FileReader();
          fileReader.onload = function () {
            insertFn(this.result, '', '');
          };
          fileReader.readAsDataURL(file);
        }
      }
    },
    // 自定义粘贴
    customPaste(editor: IDomEditor, evt: ClipboardEvent) {
      // 图片
      if (evt.clipboardData?.files?.length) {
        const file = evt.clipboardData.files[0];
        if (/\.(png)|(jpg)|(jpeg)|(gif)$/i.test(file.name)) {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            const fileKey = fileReader.result as string;
            let fileUrl = getCache(fileKey);
            const imageId = 'L' + uuid(32);
            function _func() {
              putCache(fileKey, fileUrl);
              editor.insertNode({
                type: 'image',
                src: fileUrl,
                alt: imageId,
                children: [{ text: '' }]
              } as any);
              setTimeout(() => {
                const image = document.querySelector(
                  `img[alt="${imageId}"]`
                ) as HTMLImageElement;
                const bcr = image.getBoundingClientRect();
                editor.undo!();
                editor.insertNode({
                  type: 'image',
                  src: fileUrl,
                  style: { width: bcr.width + 'px', height: bcr.height + 'px' },
                  children: [{ text: '' }]
                } as any);
              }, 350);
            }
            if (fileUrl) {
              _func();
            } else {
              fileUtil
                .uploadFile(FileUploadPkConfig.editorImage, file)
                .then((data: any) => {
                  if (data && (fileUrl = data.fileUrl)) {
                    _func();
                  }
                });
            }
          };
          fileReader.readAsDataURL(file);

          return false;
        }
      }
      // 阻止默认的粘贴行为
      evt.preventDefault();
      const html = evt.clipboardData?.getData('text/html');
      if (html === '') {
        const text = evt.clipboardData?.getData('text/plain');
        if (text === null || text === undefined || text === '') {
          return false;
        }
        const descendants: LzdDescendant[] = [];
        text.split(/\r?\n/).forEach((d, i) => {
          if (i === 0) {
            editor.insertNode({ text });
          } else {
            descendants.push({
              type: 'paragraph',
              children: [{ text: d }]
            });
          }
        });
        SlateTransforms.insertNodes(editor, descendants);
        return false;
      }
      // 包含数学变量字符
      if (
        html?.includes('Cambria Math') ||
        html?.includes('DejaVu Math TeX Gyre')
      ) {
        const text = evt.clipboardData?.getData('text/plain');
        if (text === null || text === undefined) {
          return false;
        }
        const descendants: LzdDescendant[] = [];
        text.split(/\r?\n/).forEach((d, i) => {
          if (i === 0) {
            editor.insertNode({ text });
          } else {
            descendants.push({
              type: 'paragraph',
              children: [{ text: d }]
            });
          }
        });
        SlateTransforms.insertNodes(editor, descendants);
        return false;
      }
      const htmlParser = new DOMParser();
      const htmlEl = htmlParser.parseFromString(html as string, 'text/html');
      const _fn = (el: HTMLElement) => {
        if (!el) return;
        // const { fontSize, fontWeight, fontStyle, fontFamily } = el.style;
        el.setAttribute('style', '');
        // el.style.fontSize = fontSize;
        // el.style.fontWeight = fontWeight;
        // el.style.fontStyle = fontStyle;
        // el.style.fontFamily = fontFamily;
        Array.from(el.children).forEach((d) => _fn(d as HTMLElement));
      };
      _fn(htmlEl.body);
      editor.dangerouslyInsertHtml(htmlEl.body.innerHTML);
      return false;
    }
  };
}

/** 粘贴仅文本 */
export function customPasteOnlyText(
  editorType: string,
  editor: IDomEditor,
  e: ClipboardEvent
) {
  // 阻止默认的粘贴行为
  e.preventDefault();
  const text = e.clipboardData?.getData('text/plain');
  if (text === null || text === undefined || text === '') {
    return false;
  }
  if ('input' === editorType) {
    editor.insertText(text.replace(/\r?\n/g, ''));
    return false;
  }
  const elBox = document.createDocumentFragment();
  text.split(/\r?\n/).forEach((d, i) => {
    if (i === 0) {
      const span = document.createElement('span');
      span.textContent = d;
      elBox.appendChild(span);
    } else {
      const p = document.createElement('p');
      p.textContent = d;
      elBox.appendChild(p);
    }
  });
  const div = document.createElement('div');
  div.appendChild(elBox);
  editor.dangerouslyInsertHtml(div.innerHTML);
  div.remove();
  return false;
}

/** 判断编辑器内容是否为空白 */
export function editorContentIsBlock(
  data: LzdDescendant[] | string | IDomEditor,
  type: 'html' | 'json' | 'text'
) {
  if (typeof data === 'undefined') {
    console.error('data must not be undefined');
    return true;
  }
  if (typeof data === 'string') {
    if (type === 'text') {
      return /\S/.test(data) ? false : true;
    }
    if (type === 'html') {
      const htmlEl = new DOMParser().parseFromString(
        data as string,
        'text/html'
      );
      const textContent = htmlEl.body.textContent;
      if (textContent != null && /\S/.test(textContent)) {
        return false;
      }
      if (htmlEl.body.querySelector('image') != null) {
        return false;
      }
      if (htmlEl.body.querySelector('table') != null) {
        return false;
      }
    }
    data = JSON.parse(data) as LzdDescendant[];
  }
  // 不是数组
  else if (!Array.isArray(data)) {
    data = (data as IDomEditor).children;
  }

  for (let i = 0; i < data.length; i++) {
    const item = data[i];
    if (item.type === 'table') {
      return false;
    }
    if (item.children) {
      for (let j = 0; j < item.children.length; j++) {
        const childItem = item.children[j];
        if (
          typeof childItem.text !== 'undefined' &&
          /\S/.test(childItem.text)
        ) {
          return false;
        }
        if (childItem.type === 'image' || childItem.type === 'table') {
          return false;
        }
      }
    }
  }

  return true;
}
